import type { PluginEntry } from '../../remotes/registry';
import type { Tool } from '../../links/growthPlatformsLinks';
import { APP_CATEGORIES, categoryById, type AppCategoryId } from './appStoreVocabulary';

/**
 * Pure helpers behind the App Store launcher (ADR 0028). No React and no I/O,
 * so every edge case is unit-tested here rather than through a rendered page.
 */

/**
 * The given name to greet a user by, or null when there isn't a usable one.
 *
 * Entra/Deloitte directory names commonly arrive as "Last, First" ("Garland,
 * Matthew"), so a comma means the given name comes AFTER it. An IdP with no name
 * claim sometimes fills the name with the email, which is never a greeting.
 * Trailing punctuation is dropped, so "Smith, John, Jr." greets "John", not "John,".
 */
export function firstName(name: string | null | undefined): string | null {
  const raw = (name ?? '').trim();
  if (!raw) return null;
  const given = raw.includes(',') ? raw.slice(raw.indexOf(',') + 1).trim() : raw;
  const word = given.split(/\s+/)[0] ?? '';
  if (word.includes('@')) return null;
  const first = word.replace(/[^\p{L}\p{N}]+$/u, '');
  return first || null;
}

/** "Good morning, Matt." by the local clock, or "Welcome." with no name. */
export function greeting(now: Date, name: string | null): string {
  if (!name) return 'Welcome.';
  const hour = now.getHours();
  const part = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  return `Good ${part}, ${name}.`;
}

/**
 * Two-character tile mark: initials of the first two words, or the first two
 * characters of a single word. Letters and digits only; "?" if none remain.
 */
export function monogram(displayName: string): string {
  const words = displayName
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);
  const [first, second] = words;
  if (!first) return '?';
  if (!second) return first.slice(0, 2).toUpperCase();
  return (first.charAt(0) + second.charAt(0)).toUpperCase();
}

/**
 * Case-insensitive substring match on what a tile shows: name, description,
 * tagline, owner team, and the category's label (not its id). Deliberately
 * `includes`, not a RegExp: a user typing "c++" or "(" must not throw.
 */
export function filterApps(apps: readonly PluginEntry[], query: string): PluginEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...apps];
  return apps.filter((a) =>
    [a.displayName, a.description, a.tagline, a.ownerTeam, categoryById(a.category)?.label].some((field) =>
      (field ?? '').toLowerCase().includes(q),
    ),
  );
}

/** The launcher's category filter: one category, or everything. */
export type CategoryFilter = AppCategoryId | 'all';

/**
 * Categories at least one app uses, in display order. An id this build doesn't
 * know (hand-edited data, a category removed later) counts as no category.
 */
export function categoriesInUse(apps: readonly PluginEntry[]): AppCategoryId[] {
  const used = new Set<string>();
  for (const a of apps) if (categoryById(a.category)) used.add(a.category as string);
  return APP_CATEGORIES.map((c) => c.id).filter((id) => used.has(id));
}

/** Apps in one category, or a copy of all of them. */
export function filterByCategory(apps: readonly PluginEntry[], category: CategoryFilter): PluginEntry[] {
  return category === 'all' ? [...apps] : apps.filter((a) => a.category === category);
}

export type CategoryCounts = Record<AppCategoryId, number> & { all: number };

/** How many apps each chip would show. Apps with unknown ids count only toward "all". */
export function categoryCounts(apps: readonly PluginEntry[]): CategoryCounts {
  const counts: CategoryCounts = { all: apps.length, insights: 0, pipeline: 0, strategy: 0, search: 0 };
  for (const a of apps) {
    const c = categoryById(a.category);
    if (c) counts[c.id] += 1;
  }
  return counts;
}

/**
 * The launcher search applied to the Growth Platforms tools: case-insensitive substring
 * match on what the tile shows — name, blurb, and the category's label (not its id) —
 * mirroring filterApps.
 */
export function filterTools(tools: readonly Tool[], query: string): Tool[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...tools];
  return tools.filter((t) =>
    [t.name, t.blurb, categoryById(t.category)?.label].some((field) => (field ?? '').toLowerCase().includes(q)),
  );
}
